import { PointCloudObject } from "@/object-cache";
import { selectClippingBox } from "@/store/clipping-box-selectors";
import { selectIpxActiveClippingBox } from "@/store/modes/ipx-order-mode-selectors";
import {
  setIpxFloorEstimate,
  setIpxFloorsUpdating,
  setIpxRegionExportBytes,
} from "@/store/modes/ipx-order-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { GUID } from "@faro-lotv/foundation";
import { useEffect, useMemo, useState } from "react";
import { IPX_MOCK_FALLBACK_TOTAL_BYTES } from "../ipx-config";
import { getElementFileSize } from "../ipx-size-estimate";
import { estimateRegionBytes, fetchFloorSample } from "./fetch-floor-sample";
import {
  FloorSample,
  FloorSegmentation,
  segmentFloors,
} from "./floor-segmentation";

/** Wait for the selection to settle before segmenting again */
const DEBOUNCE_MS = 150;

/**
 * Detects the floors of the region and reports their area to the wizard state
 *
 * @param pointCloud The point cloud of the dataset
 * @param hiddenScans The scans that are not sent
 * @returns the floors of the region, undefined until detected
 */
export function useIpxFloorEstimate(
  pointCloud: PointCloudObject,
  hiddenScans: GUID[],
): FloorSegmentation | undefined {
  const dispatch = useAppDispatch();

  // The default box is still being computed
  const isBoxPending = useAppSelector(
    (state) =>
      state.ipxOrderMode.isClippingBoxEnabled && !selectClippingBox(state),
  );

  useEffect(() => {
    dispatch(setIpxFloorEstimate({ status: "computing", floors: [] }));
  }, [dispatch, pointCloud]);

  const [sample, setSample] = useState<FloorSample>();
  useEffect(() => {
    // Downloading meanwhile would slow down the points the default box waits for
    if (isBoxPending) return;
    let isCurrent = true;
    fetchFloorSample(pointCloud).then(
      (sample) => isCurrent && setSample(sample),
      (error: unknown) => {
        console.warn("Unable to sample the point cloud floors", error);
        if (isCurrent) {
          dispatch(setIpxFloorEstimate({ status: "failed", floors: [] }));
        }
      },
    );
    return () => {
      isCurrent = false;
    };
  }, [dispatch, isBoxPending, pointCloud]);

  const box = useAppSelector(selectIpxActiveClippingBox);
  const sentScans = useAppSelector((state) => state.ipxOrderMode.sentScans);
  const hiddenScanIndices = useMemo(
    () =>
      new Set(
        hiddenScans.flatMap((id) => pointCloud.scanIdsToIndex?.[id] ?? []),
      ),
    [hiddenScans, pointCloud],
  );

  const [segmentation, setSegmentation] = useState<FloorSegmentation>();
  useEffect(() => {
    if (!sample || isBoxPending) return;
    dispatch(setIpxFloorsUpdating());
    // A newer selection stops the running analysis
    const abort = new AbortController();
    async function run(floorSample: FloorSample): Promise<void> {
      try {
        const input = {
          sample: floorSample,
          box,
          hiddenScans: hiddenScanIndices,
          scanners: sentScans.map(({ id, position }) => ({
            position,
            scanIndex: pointCloud.scanIdsToIndex?.[id],
          })),
        };
        // Temporary, lets the sample be downloaded from the console for offline tuning: remove before merging
        Object.assign(window, { ipxFloorInput: input });
        const result = await segmentFloors(input, abort.signal);
        if (abort.signal.aborted) return;
        setSegmentation(result);
        dispatch(
          setIpxRegionExportBytes(
            estimateRegionBytes(
              getElementFileSize(pointCloud.iElement) ??
                IPX_MOCK_FALLBACK_TOTAL_BYTES,
              floorSample,
              result.pointsInRegion,
            ),
          ),
        );
        dispatch(
          setIpxFloorEstimate({
            status: "done",
            floors: result.floors.map(({ elevation, area }) => ({
              elevation,
              area,
            })),
          }),
        );
      } catch (error) {
        if (abort.signal.aborted) return;
        console.warn("Unable to detect the floors", error);
        setSegmentation(undefined);
        // The size of an earlier region would be wrong, the box ratio estimate is used instead
        dispatch(setIpxRegionExportBytes(undefined));
        dispatch(setIpxFloorEstimate({ status: "failed", floors: [] }));
      }
    }
    const timeout = setTimeout(() => void run(sample), DEBOUNCE_MS);
    return () => {
      clearTimeout(timeout);
      abort.abort();
    };
  }, [
    box,
    dispatch,
    hiddenScanIndices,
    isBoxPending,
    pointCloud,
    sample,
    sentScans,
  ]);

  return sample ? segmentation : undefined;
}
